import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMovies, useMovieSearch } from '../hooks/useMovie';
import { toTitles, slugify } from '../lib/catalogue';
import { TitleCard } from '../components/title-card';
import { PageMasthead } from '../components/page-masthead';
import { SubscribeBand } from '../components/subscribe-band';
import { TitleMeta } from '../components/title-meta';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const SORT_OPTIONS = [
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Most Episodes', value: 'episodes' },
  { label: 'A – Z', value: 'az' },
];

export default function BrowsePage() {
  usePageTitle('Browse · Kast', 'Browse the full Kast catalogue of movies and series.');
  const { data: allMovies = [] } = useMovies();
  const [searchParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeType, setActiveType] = useState('All'); // 'All' | 'Movie' | 'Series'
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const categories = useMemo(() => {
    const cats = [...new Set(allMovies.map((m) => m.CategoryName).filter(Boolean))];
    return ['All', ...cats];
  }, [allMovies]);

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) {
      // Category links pass a slugified (lowercased) name, e.g. "adventure" for "Adventure",
      // so match case/slug-insensitively against the real CategoryName values.
      const match = categories.find(
        (c) => c.toLowerCase() === cat.toLowerCase() || slugify(c) === cat.toLowerCase()
      );
      setActiveCategory(match || cat);
    }
    const typ = searchParams.get('type');
    if (typ) setActiveType(typ);
    const s = searchParams.get('sort');
    if (s && SORT_OPTIONS.some((o) => o.value === s)) setSort(s);
  }, [searchParams, categories]);

  const categoryIdByName = useMemo(() => {
    const map = {};
    for (const m of allMovies) {
      if (m.CategoryName && m.CategoryId != null) map[m.CategoryName] = m.CategoryId;
    }
    return map;
  }, [allMovies]);

  const activeCategoryId = activeCategory !== 'All' ? categoryIdByName[activeCategory] : undefined;

  const { data: results = [], isLoading } = useMovieSearch(debouncedQuery, activeCategoryId);

  const formattedTitles = useMemo(() => {
    let list = Array.isArray(results) ? results : results?.data || [];

    // Filter by type if set
    if (activeType !== 'All') {
      list = list.filter((b) => (b.ContentType || (b.isSeries ? 'Series' : 'Movie')) === activeType);
    }

    // Sort
    const sorted = [...list];
    switch (sort) {
      case 'oldest':
        sorted.sort((a, b) => new Date(a.CreatedAt || 0) - new Date(b.CreatedAt || 0));
        break;
      case 'episodes':
        sorted.sort((a, b) => (b.EpisodeCount || 0) - (a.EpisodeCount || 0));
        break;
      case 'az':
        sorted.sort((a, b) => (a.Title || '').localeCompare(b.Title || ''));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => new Date(b.CreatedAt || 0) - new Date(a.CreatedAt || 0));
        break;
    }

    return toTitles(sorted);
  }, [results, activeType, sort]);

  return (
    <div className="flex flex-col min-h-screen">
      <PageMasthead
        title={activeCategory !== 'All' ? activeCategory : activeType !== 'All' ? (activeType === 'Movie' ? 'Movies' : 'TV Series') : 'Catalogue'}
        lead="Explore our collection of films, series, and exclusives streaming on Kast."
        meta={
          <TitleMeta
            className="text-sm"
            items={[
              `${formattedTitles.length} titles`,
              activeCategory !== 'All' ? activeCategory : 'All Categories',
              SORT_OPTIONS.find((s) => s.value === sort)?.label || 'Sorted',
            ]}
          />
        }
      />

      <div className="mx-auto max-w-[1600px] w-full gutter pb-16">
        {/* Controls Bar: Type tabs + Search + Sort */}
        <div className="flex flex-col gap-4 border-b border-white/8 pb-6 md:flex-row md:items-center md:justify-between">
          {/* Type filters */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
            {['All', 'Movie', 'Series'].map((t) => (
              <button
                key={t}
                onClick={() => setActiveType(t)}
                className={cn(
                  'rounded-full px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-200 cursor-pointer',
                  activeType === t
                    ? 'brand-sweep text-white shadow-md'
                    : 'border border-white/12 text-muted-foreground hover:border-white/25 hover:text-foreground'
                )}
              >
                {t === 'All' ? 'All Content' : t === 'Movie' ? 'Movies' : 'TV Series'}
              </button>
            ))}
          </div>

          {/* Search & Sort */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Input */}
            <div className="relative min-w-[200px] flex-1 sm:w-64 sm:flex-none">
              <Icon
                icon="ph:magnifying-glass-bold"
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Search titles..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-10 w-full rounded-full border border-white/15 bg-white/5 pl-9 pr-8 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground focus:border-white/30 focus:bg-white/10"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <Icon icon="ph:x-bold" className="size-3.5" />
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 text-sm font-medium text-foreground/90 transition-colors hover:border-white/30 hover:bg-white/10 focus:outline-none"
                >
                  <Icon icon="ph:funnel-simple-bold" className="size-4 text-muted-foreground" />
                  <span>{SORT_OPTIONS.find((s) => s.value === sort)?.label}</span>
                  <Icon icon="ph:caret-down-bold" className="size-3 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl border border-white/12 bg-popover/95 p-1 backdrop-blur-xl">
                {SORT_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setSort(opt.value)}
                    className={cn(
                      'flex items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer',
                      sort === opt.value
                        ? 'bg-white/10 font-semibold text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {opt.label}
                    {sort === opt.value && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Category Pills Scroller */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar py-4">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={cn(
                'whitespace-nowrap rounded-full px-3.5 py-1 text-xs font-medium transition-all duration-200 cursor-pointer',
                activeCategory === cat
                  ? 'bg-white/20 text-white border border-white/30'
                  : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white border border-transparent'
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Content Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 pt-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-[2/3] w-full rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : formattedTitles.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 pt-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {formattedTitles.map((title) => (
              <TitleCard key={title.id} title={title} />
            ))}
          </div>
        ) : (
          <div className="py-24 text-center">
            <Icon icon="ph:magnifying-glass" className="mx-auto size-12 text-muted-foreground/40" />
            <h3 className="display-sm mt-4 text-xl font-bold">No titles found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
          </div>
        )}
      </div>

      <SubscribeBand />
    </div>
  );
}
